"use client";

import { useEffect, useState } from "react";
import { Check, Edit, MapPin, Plus, Trash2, X } from "lucide-react";

import {
  deleteAddress,
  getAddresses,
  type Address,
  type AddressesResponse,
} from "@/lib/api/business-api";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { API_CONFIG } from "@/lib/config/api";

import { AddressForm } from "./AddressForm";
import { ConfirmDialog } from "./ConfirmDialog";

interface AddressesViewProps {
  userData: any;
  businessId?: string;
}

const headingFontStyle = { fontFamily: "var(--brand-heading-font, inherit)" };

export function AddressesView({
  userData,
  businessId = API_CONFIG.DEFAULT_BUSINESS_ID,
}: AddressesViewProps) {
  const { t } = usePageTranslation();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingAddresses, setDeletingAddresses] = useState<Set<string>>(
    new Set()
  );
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const accessToken = userData.accessToken;
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      const response: AddressesResponse = await getAddresses(
        accessToken,
        businessId
      );

      if (response.status && response.data) {
        setAddresses(response.data.addresses);
      } else {
        throw new Error(t("account.fetchAddressesFailed"));
      }
    } catch (err: any) {
      console.error("Error fetching addresses:", err);
      setError(err.message || t("account.loadAddressesFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (addressId: string) => {
    setAddressToDelete(addressId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setAddressToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!addressToDelete) return;

    setDeleteConfirmOpen(false);

    try {
      const accessToken = userData.accessToken;
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      // Mark address as deleting
      setDeletingAddresses((prev) => new Set(prev).add(addressToDelete));

      // Delete from backend
      await deleteAddress(accessToken, addressToDelete, businessId);

      // Only remove from state after successful deletion
      const updatedAddresses = addresses.filter(
        (address) => address.id !== addressToDelete
      );
      setAddresses(updatedAddresses);

      // Remove from deleting state
      setDeletingAddresses((prev) => {
        const newSet = new Set(prev);
        newSet.delete(addressToDelete);
        return newSet;
      });

      setAddressToDelete(null);
    } catch (err: any) {
      console.error("Error deleting address:", err);
      setError(t("account.deleteAddressFailed"));

      // Remove from deleting state on error
      setDeletingAddresses((prev) => {
        const newSet = new Set(prev);
        newSet.delete(addressToDelete);
        return newSet;
      });

      setAddressToDelete(null);
    }
  };

  const handleOpenAddForm = () => {
    setEditingAddress(null);
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (address: Address) => {
    setEditingAddress(address);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingAddress(null);
  };

  const handleFormSuccess = (address: Address) => {
    console.log("handleFormSuccess called with:", address);
    console.log("editingAddress:", editingAddress);

    if (editingAddress) {
      // Update existing address in the list
      console.log("Updating existing address");
      setAddresses((prev) =>
        prev.map((a) => (a.id === address.id ? address : a))
      );
    } else {
      // Add new address to the list
      console.log("Adding new address to list");
      setAddresses((prev) => [...prev, address]);
    }
  };

  const sectionTitle = (
    <h2 className="text-2xl font-bold text-brand-fg" style={headingFontStyle}>
      {t("account.myAddresses")}
    </h2>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="addresses-section">
        <div className="mb-6">{sectionTitle}</div>
        <div className="flex items-center justify-center rounded-brand border border-brand-border bg-brand-surface p-12">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="addresses-section">
        <div className="mb-6">{sectionTitle}</div>
        <div className="rounded-brand border border-brand-danger/30 bg-brand-danger/10 p-6 text-center">
          <X className="mx-auto mb-4 h-12 w-12 text-brand-danger" />
          <p className="mb-4 text-lg font-semibold text-brand-danger">{error}</p>
          <button
            onClick={fetchAddresses}
            className="rounded-brand bg-brand-danger px-6 py-2 font-semibold text-brand-danger-foreground transition-colors hover:bg-brand-danger/90"
          >
            {t("common.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!isLoading && addresses.length === 0) {
    return (
      <div className="addresses-section">
        <div className="mb-6">{sectionTitle}</div>
        <div className="rounded-brand border border-brand-border bg-brand-fg/5 p-12 text-center">
          <MapPin className="mx-auto mb-4 h-16 w-16 text-brand-muted" />
          <p className="mb-2 text-xl font-semibold text-brand-fg">
            {t("account.noAddresses")}
          </p>
          <p className="mb-6 text-brand-muted">
            {t("account.noAddressesMessage")}
          </p>
          <button
            onClick={handleOpenAddForm}
            className="inline-flex items-center gap-2 rounded-brand bg-brand-primary px-6 py-3 font-semibold text-brand-primary-foreground transition-colors hover:bg-brand-primary-hover"
          >
            <Plus className="h-5 w-5" />
            {t("checkout.addNewAddress")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="addresses-section">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {sectionTitle}
          <p className="mt-1 text-sm text-brand-muted">
            {addresses.length === 1
              ? t("account.addressCountOne")
              : t("account.addressCount", { count: addresses.length })}
          </p>
        </div>

        <button
          onClick={handleOpenAddForm}
          className="inline-flex items-center gap-2 rounded-brand bg-brand-primary px-6 py-3 font-semibold text-brand-primary-foreground transition-colors hover:bg-brand-primary-hover"
        >
          <Plus className="h-5 w-5" />
          {t("checkout.addNewAddress")}
        </button>
      </div>

      {/* Addresses Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {addresses.map((address) => {
          const isDeleting = deletingAddresses.has(address.id);
          return (
            <div
              key={address.id}
              className={`group relative overflow-hidden rounded-brand-lg border-2 bg-brand-surface p-6 shadow-sm transition-all ${
                address.is_default
                  ? "border-brand-primary/60"
                  : "border-brand-border"
              } ${
                isDeleting
                  ? "pointer-events-none opacity-60"
                  : "hover:shadow-lg"
              }`}
            >
              {/* Default Badge */}
              {address.is_default && (
                <div className="absolute top-4 end-4 flex items-center gap-1 rounded-full bg-brand-primary/10 px-3 py-1 text-xs font-semibold text-brand-primary">
                  <Check className="h-3 w-3" />
                  {t("checkout.defaultBadge")}
                </div>
              )}

              {/* Address Label */}
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-brand-primary" />
                  <h3 className="text-lg font-bold text-brand-fg">
                    {address.label}
                  </h3>
                </div>
              </div>

              {/* Address Details */}
              <div className="mb-4 space-y-2 text-sm text-brand-fg">
                <p className="font-semibold text-brand-fg">
                  {address.first_name} {address.last_name}
                </p>
                <p>{address.address_line_1}</p>
                {address.address_line_2 && <p>{address.address_line_2}</p>}
                <p>
                  {address.city}
                  {address.county && `, ${address.county}`}
                </p>
                <p>
                  {address.postcode}, {address.country}
                </p>
              </div>

              {/* Contact Info */}
              <div className="mb-4 space-y-1 text-sm text-brand-muted">
                <p>
                  {t("orderSuccess.phoneLabel")}{" "}
                  <span dir="ltr">{address.phone}</span>
                </p>
                <p>
                  {t("orderSuccess.emailLabel")}{" "}
                  <span dir="ltr">{address.email}</span>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 border-t border-brand-border pt-4">
                <button
                  onClick={() => handleOpenEditForm(address)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-brand border border-brand-primary bg-brand-surface px-4 py-2 font-semibold text-brand-primary transition-all hover:bg-brand-primary/10"
                  title={t("account.edit")}
                >
                  <Edit className="h-4 w-4" />
                  {t("account.edit")}
                </button>
                <button
                  onClick={() => handleDeleteClick(address.id)}
                  disabled={isDeleting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-brand border border-brand-danger bg-brand-surface px-4 py-2 font-semibold text-brand-danger transition-all hover:bg-brand-danger/10 disabled:cursor-not-allowed disabled:opacity-50"
                  title={isDeleting ? t("account.deleting") : t("account.delete")}
                >
                  {isDeleting ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-danger border-t-transparent" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  {isDeleting ? t("account.deleting") : t("account.delete")}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Address Form Modal */}
      <AddressForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        address={editingAddress}
        accessToken={userData.accessToken}
        businessId={businessId}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title={t("account.deleteAddressTitle")}
        message={t("account.deleteAddressConfirm")}
        confirmText={t("account.delete")}
        cancelText={t("common.cancel")}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
      />
    </div>
  );
}
